import { useEffect, useState } from 'react';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import type { InfiniteData, Query } from '@tanstack/react-query';

import type {
  GetPeopleOrdersQuery,
  OrderItemFragmentFragment
} from '@schema/cart';
import { GetPeopleOrdersFilters } from '@schema/cart';

import API from '@api';
import APIStage from '@api/graphqlStageOld';
import {
  ELEMENTS_ORDERS_LK,
  ELEMENTS_ORDERS_LK_MAIN
} from '@constants/ElementsLimit';
import { getProductCanBeBought } from '@utils/getProductCanBeBought';
import isCommentApplied from '@utils/orderUtils/isCommentApplied';

export type PeopleOrderT = OrderItemFragmentFragment & {
  fio: string | null | undefined;
  phone: string | null | undefined;
  isPickup: boolean | null | undefined;
};

export const PEOPLE_ORDERS_QUERY_KEY = 'peopleOrders';

const mapOrders = (
  data: GetPeopleOrdersQuery['getPeopleOrders']
): PeopleOrderT[] =>
  (data?.ordersList?.map(item => {
    const ordersItems = (item?.ordersItems ?? []).map(orderItem => ({
      ...orderItem,
      canBeBought: getProductCanBeBought({
        isProductMenu: !!orderItem?.catalogItem?.isMenu,
        isNotForSale: !!orderItem?.catalogItem?.is_not_for_sale,
        pathname: location.pathname
      })
    }));

    if (item?.deliveryStoreItem?.type === 'shop') {
      return {
        ...item,
        ordersItems,
        fio: item?.peopleItem?.fio,
        phone: item?.peopleItem?.phone,
        isPickup: true
      };
    }
    return { ...item, ordersItems };
  }) as PeopleOrderT[]) || [];

export const peopleOrdersInfiniteQueryOptions = (
  filter: GetPeopleOrdersFilters
) =>
  infiniteQueryOptions({
    queryKey: [PEOPLE_ORDERS_QUERY_KEY, filter],
    queryFn: ({ pageParam }) =>
      API.getPeopleOrders({
        page: pageParam,
        setLimit: ELEMENTS_ORDERS_LK,
        withServices: true,
        filter,
        includeRecipes: true
      }).then(res => ({
        orders: mapOrders(res.getPeopleOrders),
        totalCount: res.getPeopleOrders?.totalCount || 0,
        orderThatUserCanChangeId:
          res.getPeopleOrders?.orderThatUserCanChange?.id ?? null
      })),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPageParam * ELEMENTS_ORDERS_LK < lastPage.totalCount
        ? lastPageParam + 1
        : undefined,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

/**
 * Последние заказы для меню на главной: мобильное и десктопное меню
 * читают один кэш вместо трёх отдельных запросов getPeopleOrders.
 * totalCount — общее число заказов (бейдж «N заказов»).
 * staleTime не задаём (0): данные о заказах всегда перезапрашиваются
 * на маунте/фокусе — одновременные подписчики дедуплицируются in-flight,
 * а кэш (gcTime по умолчанию) даёт мгновенную отрисовку до ответа.
 */
export const lastOrdersQueryOptions = () =>
  queryOptions({
    queryKey: [PEOPLE_ORDERS_QUERY_KEY, 'last'],
    queryFn: () =>
      API.getPeopleOrders({
        page: 1,
        setLimit: ELEMENTS_ORDERS_LK_MAIN,
        withServices: true,
        filter: GetPeopleOrdersFilters.All,
        includeRecipes: false
      }).then(res => ({
        orders: mapOrders(res.getPeopleOrders),
        totalCount: res.getPeopleOrders?.totalCount || 0,
        orderThatUserCanChangeId:
          res.getPeopleOrders?.orderThatUserCanChange?.id ?? null
      }))
  });

/**
 * Последние 10 заказов бар-меню за сегодня. staleTime 5с заменяет ручной
 * throttle из BarMenuStore (last10OrdersFetchedAt), дедупликацию
 * одновременных вызовов даёт сам react-query. null — гость без заказов.
 */
export const barMenuTodayOrdersQueryOptions = () =>
  queryOptions({
    queryKey: [PEOPLE_ORDERS_QUERY_KEY, 'barMenuToday'],
    queryFn: () =>
      API.getPeopleOrders({
        page: 1,
        setLimit: 10,
        filter: GetPeopleOrdersFilters.MenuToday,
        includeRecipes: false
      }).then(res => res.getPeopleOrders ?? null),
    staleTime: 5000
  });

export const peopleOrderByIdQueryOptions = (orderId: number) =>
  queryOptions({
    queryKey: [PEOPLE_ORDERS_QUERY_KEY, 'byId', orderId],
    queryFn: () =>
      API.getPeopleOrderById({ orderId, includeRecipes: false }).then(
        res => res.getPeopleOrderById?.orderItem ?? null
      )
  });

/**
 * «Редактировать заказ» (страница «спасибо за покупку»): createCartFromOrder
 * трогает корзину — это мутация, не query. Свежий canOrderBeChanged пишется
 * в кэш заказа; навигация в ЛК — на стороне компонента (onSuccess у mutate).
 * Возвращает актуальный canOrderBeChanged.
 */
export const useEditOrderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: number) => {
      const [orderRes] = await Promise.all([
        API.getPeopleOrderById({ orderId, includeRecipes: false }),
        APIStage.createCartFromOrder({ orderId })
      ]);
      return !!orderRes?.getPeopleOrderById?.orderItem?.canOrderBeChanged;
    },
    onSuccess: (canOrderBeChanged, orderId) => {
      queryClient.setQueryData(
        peopleOrderByIdQueryOptions(orderId).queryKey,
        prev => (prev ? { ...prev, canOrderBeChanged } : prev)
      );
    }
  });
};

export const CONFIRM_ORDER_QUERY_KEY = 'confirmOrderById';

/**
 * Заказ на странице «Спасибо за покупку» (доступ по hash из URL, работает
 * и для гостя). Без staleTime — статус заказа всегда свежий, рефетч на
 * маунте/фокусе, дедуп только in-flight (то же решение, что у
 * getPeopleOrders). null — заказ не найден/hash невалиден.
 */
export const confirmOrderByIdQueryOptions = (params: {
  orderId: number;
  hash: string;
}) =>
  queryOptions({
    queryKey: [CONFIRM_ORDER_QUERY_KEY, params.orderId, params.hash],
    queryFn: () =>
      API.confirmOrderById({
        orderId: params.orderId,
        hash: params.hash
      }).then(res => res.confirmOrderById ?? null)
  });

export const ORDER_ADDRESS_COMMENTS_QUERY_KEY = 'orderAndAddressComments';

export type OrderExtraInfoT = {
  isPrivateHouse: boolean;
  optionsArray: Array<{ id: number; title: string }>;
} | null;

// module-level select: маппинг комментариев заказа/адреса в блок
// «Дополнительно» страницы «Спасибо за покупку» (порт из компонента)
const selectOrderExtraInfo = (
  res: Awaited<ReturnType<typeof API.getOrderAndAddressComments>>
): OrderExtraInfoT => {
  if (!res.getOrderAndAddressComments) {
    return null;
  }
  const { order, address } = res.getOrderAndAddressComments;
  const commentItems = address?.commentItems;
  let isPrivateHouse = false;
  const extraInfoAddress = commentItems?.length
    ? commentItems
        .filter(i => {
          if (!isCommentApplied(i)) return false;
          if (i?.comment?.id === 37) {
            isPrivateHouse = true;
            return false;
          }
          return true;
        })
        .map(item => ({
          id: item?.option?.id || item?.comment?.id,
          title: item?.option?.title || item.comment?.title || ''
        }))
    : [];
  const extraInfoOrder = order?.commentItems?.length
    ? order.commentItems.map(i => {
        if (i?.comment?.id === 16) {
          return {
            id: i?.comment?.id,
            title: `${i?.comment?.title} ${i?.value || ''}`
          };
        }
        if (!isCommentApplied(i)) {
          return { id: undefined, title: '' };
        }
        if (!i.option?.id && i.comment?.type === 'select') {
          return { id: undefined, title: '' };
        }
        return {
          id: i.comment?.id,
          title: i.option?.title || i.comment?.title || ''
        };
      })
    : [];

  const optionsArray = [...extraInfoAddress, ...extraInfoOrder].filter(
    (i): i is { id: number; title: string } =>
      !!i?.id && typeof i?.id === 'number'
  );

  return { isPrivateHouse, optionsArray };
};

/**
 * Комментарии заказа и адреса («Дополнительно» на странице «Спасибо
 * за покупку»). Зависимый запрос: addressId приходит из confirmOrderById —
 * enabled на стороне компонента.
 */
export const orderAndAddressCommentsQueryOptions = (params: {
  orderId: number;
  addressId: number;
}) =>
  queryOptions({
    queryKey: [
      ORDER_ADDRESS_COMMENTS_QUERY_KEY,
      params.orderId,
      params.addressId
    ],
    queryFn: () =>
      API.getOrderAndAddressComments({
        orderId: params.orderId,
        addressId: params.addressId
      }),
    select: selectOrderExtraInfo
  });

/**
 * Добавление товара к уже оформленному заказу («Спасибо за покупку»).
 * Успех → рефетч confirmOrderById (invalidateQueries возвращает промис —
 * onSuccess на стороне mutate сработает после рефетча, как старая цепочка
 * addProductAfterPurchase → getOrderInfo → метрика).
 */
export const useAddProductAfterPurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: Parameters<typeof API.addProductAfterPurchase>[0]) =>
      API.addProductAfterPurchase(variables),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [CONFIRM_ORDER_QUERY_KEY] })
  });
};

/**
 * Id последнего доставленного заказа (плашка «оцените заказ»).
 * Без staleTime — всегда свежий; observable-зеркало живёт в UserStore.
 */
export const lastDeliveredOrderIdQueryOptions = () =>
  queryOptions({
    queryKey: [PEOPLE_ORDERS_QUERY_KEY, 'lastDeliveredOrderId'],
    queryFn: () => API.getPeopleLastDeliveredOrderId()
  });

interface PeopleOrdersPageT {
  orders: PeopleOrderT[];
  totalCount: number;
  orderThatUserCanChangeId: string | null;
}

type PeopleOrdersDataT = InfiniteData<PeopleOrdersPageT>;

const readTotalCount = (query: Query): number | null => {
  const data = query.state.data as PeopleOrdersDataT | undefined;
  const pages = data?.pages;
  const count = pages?.[pages.length - 1]?.totalCount;
  return typeof count === 'number' ? count : null;
};

export const usePeopleOrdersTotalCount = (): number | null => {
  const queryClient = useQueryClient();
  const [totalCount, setTotalCount] = useState<number | null>(null);

  useEffect(() => {
    const cache = queryClient.getQueryCache();

    const lastFetched = cache
      .findAll({ queryKey: [PEOPLE_ORDERS_QUERY_KEY] })
      .filter(query => query.state.data)
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];
    setTotalCount(lastFetched ? readTotalCount(lastFetched) : null);

    return cache.subscribe(event => {
      if (event.query.queryKey[0] !== PEOPLE_ORDERS_QUERY_KEY) {
        return;
      }
      if (event.type === 'added' && !event.query.state.data) {
        setTotalCount(null);
      }
      if (event.type === 'updated' && event.action.type === 'success') {
        setTotalCount(readTotalCount(event.query));
      }
      if (
        event.type === 'removed' &&
        !cache.findAll({ queryKey: [PEOPLE_ORDERS_QUERY_KEY] }).length
      ) {
        setTotalCount(null);
      }
    });
  }, [queryClient]);

  return totalCount;
};
